/**
 * The rows of «مناطق التوصيل» in the add form and the edit form of a restaurant.
 *
 * ONE ROW HAS ONE ID FOR ITS LIFE. Both forms gave a new row the LENGTH of the
 * list as its id. After a row was deleted, the next new row got an id that
 * another row had. The form then wrote the values of two rows into one of
 * them, and the other one kept its first values. On 2026-10-06 the add form
 * sent one area in two rows for that reason, the API refused the list, and
 * each new press of the button made the restaurant again: 6 times.
 *
 * A saved row has the id of its `delivery_costs` row, a number. A new row has
 * a text id that no saved row can have, so the edit form never asks the API
 * to delete a row that is not this restaurant's.
 */

const NEW_ROW_PREFIX = "new-";

/**
 * @param {{current: number}} counter - a ref that this form keeps
 * @returns {string} an id for a row that is not saved
 */
export const newRowId = (counter) => {
    counter.current += 1;
    return `${NEW_ROW_PREFIX}${counter.current}`;
};

/** True for a row that the API does not have yet. */
export const isNewRow = (id) => String(id).startsWith(NEW_ROW_PREFIX);

const isSameRow = (a, b) => String(a) === String(b);

/** The list with the values of `row`: in the place of the row with its id, or at the end. */
export const upsertRow = (rows, row) => {
    const index = rows.findIndex((item) => isSameRow(item.id, row.id));
    if (index === -1) return [...rows, row];
    return rows.map((item, position) => (position === index ? row : item));
};

/** The list without the row `id`. */
export const removeRow = (rows, id) => rows.filter((item) => !isSameRow(item.id, id));

/**
 * What stops a save of these rows, as one Arabic sentence. `null` when
 * nothing does.
 *
 * @param {Array<{area: number|string, cost?: number}>} rows
 * @param {Array<{id: number, name: string}>} [areas] - for the name of a repeated area
 * @returns {string|null}
 */
export const areaRowsProblem = (rows, areas = []) => {
    if (rows.some((row) => !(Number(row.area) > 0))) {
        return "اختر المنطقة في كل صف من مناطق التوصيل، أو احذف الصف الفارغ.";
    }
    const seen = new Set();
    for (const row of rows) {
        const area = Number(row.area);
        if (seen.has(area)) {
            const name = areas.find((item) => Number(item.id) === area)?.name;
            return `المنطقة ${name ? `«${name}» ` : ""}مكررة في مناطق التوصيل. احذف الصف المكرر.`;
        }
        seen.add(area);
    }
    return null;
};

/** One row as `POST api/costs` reads it: the area, the price, and the switch. */
export const toCostPayload = (row) => ({
    area: Number(row.area),
    cost: Number(row.cost) || 0,
    ...(typeof row.available === "boolean" ? { available: row.available } : {}),
});
